import {
  getV1CompaniesCompanyIdDepartments,
  getV1CompaniesCompanyIdDepartmentsTypeahead,
  GetV1CompaniesCompanyIdDepartmentsTypeaheadData,
  postV1CompaniesCompanyIdDepartments,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useCallback } from 'react';

import { MAX_API_PAGE_SIZE } from '~constants/consts';
import useLatestRequestSignal from '~hooks/useLatestRequestSignal/useLatestRequestSignal';
import connection from '~services/connectionModule';
import { extractPagination, PaginationLink } from '~services/pagination';
import { useStores } from '~store';

import { Department, DepartmentTypeahead } from './models';

interface FetchDepartmentsPageProps {
  companyId: string;
  cursor?: string;
  limit: number;
  link?: PaginationLink;
  signal?: AbortSignal;
}

export const useDepartment = () => {
  const { companyAssetsStore } = useStores();

  const issueRequestSignal = useLatestRequestSignal();

  const fetchDepartmentsPage = useCallback(
    async ({ companyId, cursor, limit, link, signal }: FetchDepartmentsPageProps) => {
      try {
        const response = await getV1CompaniesCompanyIdDepartments({
          path: { 'company-id': companyId },
          query: {
            'page[limit]': limit,
            ...(link && cursor ? { [`page[${link}]`]: cursor } : {}),
          },
          signal,
        });

        return {
          data: response.data.data.map(Department.parse),
          pagination: extractPagination(response),
        };
      } catch (error) {
        if (signal?.aborted) {
          throw error;
        }

        connection.handleRequestError(
          error,
          t('error_messages.departments.failed_to_fetch'),
        );
        throw error;
      }
    },
    [],
  );

  const loadDepartmentOptions = useCallback(
    async (companyId: string) => {
      const signal = issueRequestSignal();

      try {
        const { data } = await fetchDepartmentsPage({
          companyId,
          limit: MAX_API_PAGE_SIZE,
          signal,
        });

        if (signal.aborted) {
          return [];
        }

        companyAssetsStore.setDepartments(data);

        return data;
      } catch {
        return [];
      }
    },
    [companyAssetsStore, fetchDepartmentsPage, issueRequestSignal],
  );

  interface GetCompanyDepartmentsTypeaheadProps {
    companyId: string;
    limit?: number;
    query?: string;
    cursor?: string;
  }

  const getDepartmentByCompanyIdTypeahead = useCallback(
    async ({ companyId, limit, query, cursor }: GetCompanyDepartmentsTypeaheadProps) => {
      try {
        const params: Partial<GetV1CompaniesCompanyIdDepartmentsTypeaheadData['query']> =
          {
            'page[limit]': limit ?? 10,
          };

        if (query) {
          params['search[query]'] = query;
        }

        if (cursor) {
          params['page[after]'] = cursor;
        }

        const response = await getV1CompaniesCompanyIdDepartmentsTypeahead({
          path: { 'company-id': companyId },
          query: params,
        });

        const departments = response.data.data.map(DepartmentTypeahead.parse);
        const pagination = extractPagination(response);

        return { data: departments, pagination };
      } catch (error) {
        connection.handleRequestError(
          error,
          t('error_messages.departments.failed_to_fetch'),
        );
      }
    },
    [],
  );

  const createDepartment = useCallback(
    async (companyId: string, name: string) => {
      try {
        const response = await postV1CompaniesCompanyIdDepartments({
          path: { 'company-id': companyId },
          body: { name },
        });
        const department = Department.parse(response.data.data);

        companyAssetsStore.addDepartment(department);

        return department;
      } catch (error) {
        connection.handleRequestError(
          error,
          t('error_messages.departments.failed_to_create'),
        );
        throw error;
      }
    },
    [companyAssetsStore],
  );

  return {
    createDepartment,
    fetchDepartmentsPage,
    getDepartmentByCompanyIdTypeahead,
    loadDepartmentOptions,
  };
};
